import { useCallback, useEffect } from 'react';
import { Paper, Typography, Grid, Alert } from '@mui/material';
import { useDataProvider } from 'react-admin';
import { TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { z } from 'zod';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { stepValidator } from '@/components/wizard/validateWithZod';
import { buildTransitionChain, useRuleEvaluation } from '@/components/workflow';

const STEPS = [
  { id: 'period', title: 'Reporting Period' },
  { id: 'workforce', title: 'Workforce Profile (EEA2)' },
  { id: 'income', title: 'Income Differential (EEA4)' },
  { id: 'variance', title: 'Variance Analysis' },
  { id: 'review', title: 'EE Committee Review' },
];

const SECTORS = [
  'Agriculture', 'Mining', 'Manufacturing', 'Utilities', 'Construction',
  'Retail', 'Finance', 'Transport', 'Government', 'Education', 'Health', 'Other',
];

const EMPLOYER_SIZES = ['Micro (1-4)', 'Small (5-49)', 'Medium (50-149)', 'Large (150+)'];
const OCCUPATIONAL_LEVELS = ['Top Management', 'Senior Management', 'Professionally Qualified', 'Skilled Technical', 'Semi-Skilled', 'Unskilled'];
const APPROVAL_OPTIONS = ['Yes', 'No'];

interface WorkforceRow {
  occupationalLevel: string;
  africanMale: number;
  africanFemale: number;
  africanDisability: number;
  colouredMale: number;
  colouredFemale: number;
  colouredDisability: number;
  indianMale: number;
  indianFemale: number;
  indianDisability: number;
  whiteMale: number;
  whiteFemale: number;
  whiteDisability: number;
}

interface FormValues {
  reportingYear: string;
  sector: string;
  employerSize: string;
  totalEmployees: number;
  highestEarnerMale: number;
  highestEarnerFemale: number;
  lowestEarnerMale: number;
  lowestEarnerFemale: number;
  incomeRatio: number;
  gapExplanation: string;
  committeeApproval: string;
  meetingDate: string;
  minutesReference: string;
  workforceRows: WorkforceRow[];
}

const INITIAL_VALUES: FormValues = {
  reportingYear: new Date().getFullYear().toString(),
  sector: '',
  employerSize: '',
  totalEmployees: 0,
  highestEarnerMale: 0,
  highestEarnerFemale: 0,
  lowestEarnerMale: 0,
  lowestEarnerFemale: 0,
  incomeRatio: 0,
  gapExplanation: '',
  committeeApproval: '',
  meetingDate: '',
  minutesReference: '',
  workforceRows: OCCUPATIONAL_LEVELS.map(level => ({
    occupationalLevel: level,
    africanMale: 0,
    africanFemale: 0,
    africanDisability: 0,
    colouredMale: 0,
    colouredFemale: 0,
    colouredDisability: 0,
    indianMale: 0,
    indianFemale: 0,
    indianDisability: 0,
    whiteMale: 0,
    whiteFemale: 0,
    whiteDisability: 0,
  })),
};

const stepSchemas = {
  period: z.object({
    reportingYear: z.string().min(1, 'Reporting year is required'),
    sector: z.string().min(1, 'Sector is required'),
    employerSize: z.string().min(1, 'Employer size is required'),
    totalEmployees: z.number().min(1, 'Total employees is required'),
  }),
  income: z.object({
    highestEarnerMale: z.number().min(1, 'Highest male earner is required'),
    highestEarnerFemale: z.number().min(1, 'Highest female earner is required'),
    lowestEarnerMale: z.number().min(1, 'Lowest male earner is required'),
    lowestEarnerFemale: z.number().min(1, 'Lowest female earner is required'),
  }),
};

function validateStep(stepId: string, values: FormValues): { isValid: boolean; errors: Record<string, string> } {
  const schema = stepSchemas[stepId as keyof typeof stepSchemas];
  if (!schema) {
    if (stepId === 'workforce') {
      const totalCount = values.workforceRows.reduce((sum, row) => {
        return sum + row.africanMale + row.africanFemale + row.africanDisability +
          row.colouredMale + row.colouredFemale + row.colouredDisability +
          row.indianMale + row.indianFemale + row.indianDisability +
          row.whiteMale + row.whiteFemale + row.whiteDisability;
      }, 0);
      if (totalCount !== values.totalEmployees) {
        return { isValid: false, errors: { totalEmployees: `Workforce counts (${totalCount}) must equal total employees (${values.totalEmployees})` } };
      }
      return { isValid: true, errors: {} };
    }
    if (stepId === 'review') {
      const errors: Record<string, string> = {};
      if (!values.committeeApproval) errors.committeeApproval = 'Committee approval status is required';
      if (values.committeeApproval === 'Yes' && !values.meetingDate) {
        errors.meetingDate = 'Meeting date is required when approved';
      }
      return { isValid: Object.keys(errors).length === 0, errors };
    }
    return { isValid: true, errors: {} };
  }

  return stepValidator(schema)(values as unknown as Record<string, unknown>);
}

function getStepDescription(stepId: string): string {
  const descriptions: Record<string, string> = {
    period: 'Set the reporting period and employer details per Employment Equity Act',
    workforce: 'Capture workforce profile by occupational level, race, gender, and disability (EEA2)',
    income: 'Capture income differential data for EEA4 reporting',
    variance: 'Analyze variances and identify pay gaps requiring attention',
    review: 'Record EE committee review and approval',
  };
  return descriptions[stepId] || '';
}

export const EEReportWizard = () => {
  const dataProvider = useDataProvider();

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const context: Record<string, unknown> = {
      reportId: null,
      reportingYear: v.reportingYear,
      workforceData: v.workforceRows,
      eea2Data: v.workforceRows,
      eea4Data: {
        highestEarnerMale: v.highestEarnerMale,
        highestEarnerFemale: v.highestEarnerFemale,
        lowestEarnerMale: v.lowestEarnerMale,
        lowestEarnerFemale: v.lowestEarnerFemale,
        incomeRatio: v.incomeRatio,
      },
      sectoralTargets: null,
      analysisResults: { gapExplanation: v.gapExplanation },
      submittedDate: null,
      submittedBy: null,
      status: v.committeeApproval === 'Yes' ? 'approved' : 'pending',
      rejectionReason: null,
      amendments: [],
    };

    const reportResult = await dataProvider.create('ee-profiles', { data: formValues });
    const reportId = reportResult.data.id;

    const wizardEvents = ['START_DATA_COLLECTION', 'WORKFORCE_COLLECTED', 'PROFILING_COMPLETE', 'ANALYSIS_COMPLETE', 'DRAFT_COMPLETE', 'SUBMIT_FOR_REVIEW'];
    const { finalState, transitions } = buildTransitionChain('ee-report', 'initiated', wizardEvents);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'ee-report',
        entityId: reportId,
        entityName: 'EE Report: ' + v.reportingYear,
        currentState: { value: finalState, context },
        status: 'active',
        workflowVersion: '1.0',
        startedAt: new Date().toISOString(),
        lastActionAt: new Date().toISOString(),
        pausedAt: null,
        breakpointId: null,
        relatedEntityId: reportId,
        transitions,
      },
    });

    return { id: reportId };
  }, [dataProvider]);

  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isSaving,
    isCompleted,
    progress,
    values,
    errors,
    stepFeedback,
    createdId,
    nextStep,
    prevStep,
    updateValues,
    validateStep: wizardValidateStep,
    complete,
    reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const { evaluate: evaluateRule, isEvaluating: isEvaluatingRule, result: ruleResult } = useRuleEvaluation({
    ruleId: 'ee-report-guards',
    context: {
      entityId: createdId || '',
      entityType: 'process',
      currentState: currentStep?.id || 'initiated',
      event: 'SUBMIT',
      reportingYear: (values as unknown as FormValues).reportingYear,
      sector: (values as unknown as FormValues).sector,
      totalEmployees: (values as unknown as FormValues).totalEmployees,
    },
  });

  useEffect(() => {
    if (currentStep?.id === 'review') {
      evaluateRule();
    }
  }, [currentStep?.id, evaluateRule]);

  const vals = values as unknown as FormValues;

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value } as Record<string, unknown>);
  };

  const updateWorkforceRow = (index: number, field: keyof WorkforceRow, value: number) => {
    const newRows = [...vals.workforceRows];
    newRows[index] = { ...newRows[index], [field]: value };
    updateValues({ workforceRows: newRows } as Record<string, unknown>);
  };

  const handleNext = () => {
    const result = validateStep(currentStep.id, vals);
    if (!result.isValid) {
      return;
    }

    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const handleBack = () => {
    prevStep();
  };

  const handleComplete = () => {
    complete();
  };

  const handleReset = () => {
    reset();
  };

  const renderFormFields = (stepId: string) => {
    switch (stepId) {
      case 'period':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Reporting Year" type="number" value={vals.reportingYear}
                onChange={e => updateField('reportingYear', e.target.value)} fullWidth
                error={!!errors.reportingYear} helperText={errors.reportingYear} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.sector}>
                <InputLabel>Sector</InputLabel>
                <Select value={vals.sector} label="Sector"
                  onChange={e => updateField('sector', e.target.value)}>
                  {SECTORS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.employerSize}>
                <InputLabel>Employer Size</InputLabel>
                <Select value={vals.employerSize} label="Employer Size"
                  onChange={e => updateField('employerSize', e.target.value)}>
                  {EMPLOYER_SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Total Employees" type="number" value={vals.totalEmployees}
                onChange={e => updateField('totalEmployees', parseInt(e.target.value) || 0)} fullWidth
                error={!!errors.totalEmployees} helperText={errors.totalEmployees || 'Total headcount at reporting date'} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                Employment Equity Act: Employers with 50+ employees must submit EEA2 and EEA4 reports annually to the Department of Employment and Labour (DEL).
              </Alert>
            </Grid>
          </Grid>
        );

      case 'workforce':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Workforce Profile (EEA2) — Total: {vals.totalEmployees}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Enter headcount by occupational level, race, gender, and disability status
                </Typography>
              </Paper>
            </Grid>
            {vals.workforceRows.map((row, idx) => (
              <Grid key={row.occupationalLevel} size={{ xs: 12 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{row.occupationalLevel}</Typography>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">African Male</Typography>
                      <TextField type="number" size="small" fullWidth value={row.africanMale}
                        onChange={e => updateWorkforceRow(idx, 'africanMale', parseInt(e.target.value) || 0)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">African Female</Typography>
                      <TextField type="number" size="small" fullWidth value={row.africanFemale}
                        onChange={e => updateWorkforceRow(idx, 'africanFemale', parseInt(e.target.value) || 0)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Coloured Male</Typography>
                      <TextField type="number" size="small" fullWidth value={row.colouredMale}
                        onChange={e => updateWorkforceRow(idx, 'colouredMale', parseInt(e.target.value) || 0)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Coloured Female</Typography>
                      <TextField type="number" size="small" fullWidth value={row.colouredFemale}
                        onChange={e => updateWorkforceRow(idx, 'colouredFemale', parseInt(e.target.value) || 0)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Indian Male</Typography>
                      <TextField type="number" size="small" fullWidth value={row.indianMale}
                        onChange={e => updateWorkforceRow(idx, 'indianMale', parseInt(e.target.value) || 0)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Indian Female</Typography>
                      <TextField type="number" size="small" fullWidth value={row.indianFemale}
                        onChange={e => updateWorkforceRow(idx, 'indianFemale', parseInt(e.target.value) || 0)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">White Male</Typography>
                      <TextField type="number" size="small" fullWidth value={row.whiteMale}
                        onChange={e => updateWorkforceRow(idx, 'whiteMale', parseInt(e.target.value) || 0)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">White Female</Typography>
                      <TextField type="number" size="small" fullWidth value={row.whiteFemale}
                        onChange={e => updateWorkforceRow(idx, 'whiteFemale', parseInt(e.target.value) || 0)} />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
            {errors.totalEmployees && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">{errors.totalEmployees}</Alert>
              </Grid>
            )}
          </Grid>
        );

      case 'income':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Income Differential (EEA4)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Enter annual remuneration for highest and lowest earners by gender
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Highest Earner (Male) - Annual Remuneration" type="number"
                value={vals.highestEarnerMale}
                onChange={e => updateField('highestEarnerMale', parseInt(e.target.value) || 0)} fullWidth
                error={!!errors.highestEarnerMale} helperText={errors.highestEarnerMale} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Highest Earner (Female) - Annual Remuneration" type="number"
                value={vals.highestEarnerFemale}
                onChange={e => updateField('highestEarnerFemale', parseInt(e.target.value) || 0)} fullWidth
                error={!!errors.highestEarnerFemale} helperText={errors.highestEarnerFemale} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Lowest Earner (Male) - Annual Remuneration" type="number"
                value={vals.lowestEarnerMale}
                onChange={e => updateField('lowestEarnerMale', parseInt(e.target.value) || 0)} fullWidth
                error={!!errors.lowestEarnerMale} helperText={errors.lowestEarnerMale} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Lowest Earner (Female) - Annual Remuneration" type="number"
                value={vals.lowestEarnerFemale}
                onChange={e => updateField('lowestEarnerFemale', parseInt(e.target.value) || 0)} fullWidth
                error={!!errors.lowestEarnerFemale} helperText={errors.lowestEarnerFemale} />
            </Grid>
            {vals.highestEarnerMale > 0 && vals.lowestEarnerMale > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  Calculated income ratio (Male highest/lowest): {vals.incomeRatio.toFixed(2)}:1
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                Employment Equity Act Section 21: Employers must report remuneration by gender and race.
                Income differentials exceeding 1:4 ratio require explanation.
              </Alert>
            </Grid>
          </Grid>
        );

      case 'variance':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Variance Analysis</Typography>
                <Typography variant="body2" color="text.secondary">
                  Analyze workforce representation and income differentials to identify gaps requiring attention.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Gap Explanation" value={vals.gapExplanation}
                onChange={e => updateField('gapExplanation', e.target.value)} fullWidth
                multiline rows={6}
                placeholder="Identify and explain any significant variances in workforce representation or remuneration..." />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                EE Act: Areas requiring attention include underrepresentation of designated groups,
                income gaps exceeding acceptable ratios, and barriers to career progression.
              </Alert>
            </Grid>
          </Grid>
        );

      case 'review':
        return (
          <Grid container spacing={2}>
            {ruleResult && !ruleResult.allowed && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Guard Check</Typography>
                  <Typography variant="body2">{ruleResult.reason}</Typography>
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.committeeApproval}>
                <InputLabel>EE Committee Approval</InputLabel>
                <Select value={vals.committeeApproval} label="EE Committee Approval"
                  onChange={e => updateField('committeeApproval', e.target.value)}>
                  {APPROVAL_OPTIONS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </Select>
              </FormControl>
              {errors.committeeApproval && <Typography variant="caption" color="error">{errors.committeeApproval}</Typography>}
            </Grid>
            {vals.committeeApproval === 'Yes' && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="EE Committee Meeting Date" type="date" value={vals.meetingDate}
                    onChange={e => updateField('meetingDate', e.target.value)} fullWidth
                    InputLabelProps={{ shrink: true }} error={!!errors.meetingDate}
                    helperText={errors.meetingDate} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField label="Minutes Reference" value={vals.minutesReference}
                    onChange={e => updateField('minutesReference', e.target.value)} fullWidth
                    placeholder="Reference to EE committee meeting minutes document" />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <Alert severity="success">
                EE Report for {vals.reportingYear} is ready for submission to DEL.
                EEA2 and EEA4 forms will be generated from the captured data.
              </Alert>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  const renderStepContent = () => {
    if (isCompleted) {
      return (
        <OutcomeSummary
          title="EE Report Submitted"
          processId={createdId || undefined}
          fields={[
            { label: 'Reporting Year', value: vals.reportingYear },
            { label: 'Sector', value: vals.sector },
            { label: 'Employer Size', value: vals.employerSize },
            { label: 'Total Employees', value: vals.totalEmployees },
            { label: 'Income Ratio (M)', value: vals.incomeRatio.toFixed(2) },
            { label: 'Committee Approved', value: vals.committeeApproval },
            { label: 'Meeting Date', value: vals.meetingDate || 'N/A' },
          ]}
          footerActions={[
            { label: 'View Reports', to: '/eeReports' },
            { label: 'Start Another', onClick: handleReset },
          ]}
        />
      );
    }

    return (
      <>
        {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, color: 'text.secondary' }}>
            Step {currentStepIndex + 1} of {totalSteps}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            {currentStep.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {getStepDescription(currentStep.id)}
          </Typography>

          {renderFormFields(currentStep.id)}
        </Paper>
      </>
    );
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={handleBack}
      onNext={handleNext}
      onComplete={handleComplete}
      onReset={handleReset}
    >
      {renderStepContent()}
    </WizardLayout>
  );
};